import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import logger from 'logger'

const log = logger.extend('overlay-credential')

const { VITE_CONNECT_API_URL: connectApiUrl } = import.meta.env

const REFRESH_FRACTION = 0.8
const DEFAULT_TICKET_TTL_SECONDS = 600
const RETRY_NETWORK_MS = 30_000
const RETRY_CAP_MS = 60_000
const RETRY_REFUSED_MS = 5 * 60_000
const RETRY_JITTER_FRACTION = 0.2
const EXCHANGE_TIMEOUT_MS = 8000

export type OverlayLinkTargetKind = 'chat' | 'emotes' | 'merch' | 'tickerbar'

export interface OverlayLinkTarget {
  kind: OverlayLinkTargetKind
  id: string
}

export interface OverlaySocketCredentialOptions {
  k?: string | null
  requireTarget?: boolean
}

export interface OverlaySocketCredential {
  token: string | null
  target: OverlayLinkTarget | null
}

const TARGET_KINDS: readonly string[] = ['chat', 'emotes', 'merch', 'tickerbar']

const parseTarget = (raw: unknown): OverlayLinkTarget | null => {
  if (typeof raw !== 'object' || raw === null) return null
  const { kind, id } = raw as { kind?: unknown, id?: unknown }
  if (typeof kind !== 'string' || !TARGET_KINDS.includes(kind)) return null
  if (typeof id !== 'string' || id === '') return null
  return { kind: kind as OverlayLinkTargetKind, id }
}

const jittered = (ms: number): number => {
  const sample = globalThis.crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32
  return Math.round(ms * (1 - RETRY_JITTER_FRACTION + 2 * RETRY_JITTER_FRACTION * sample))
}

export const useOverlaySocketCredentialState = (
  options: OverlaySocketCredentialOptions = {}
): OverlaySocketCredential => {
  const { k: explicitK = null, requireTarget = false } = options
  const [searchParams] = useSearchParams()

  const hasExplicitK = typeof explicitK === 'string' && explicitK !== ''
  const legacyToken = hasExplicitK ? null : searchParams.get('token')
  const k = hasExplicitK ? explicitK : searchParams.get('k')
  const hasLegacyToken = legacyToken !== null && legacyToken !== ''

  const [ticket, setTicket] = useState<string | null>(null)
  const [target, setTarget] = useState<OverlayLinkTarget | null>(null)
  const ticketRef = useRef<string | null>(null)

  useEffect(() => {
    if (hasLegacyToken || k === null || k === '') return
    if (typeof connectApiUrl !== 'string' || connectApiUrl === '') {
      log({ stage: 'exchange-unconfigured', detail: 'misconfigured' })
      return
    }

    let cancelled = false
    let timer: ReturnType<typeof globalThis.setTimeout> | undefined

    const schedule = (ms: number): void => {
      if (cancelled) return
      timer = globalThis.setTimeout(() => { void exchange() }, ms)
    }

    const exchange = async (): Promise<void> => {
      try {
        const res = await fetch(`${connectApiUrl}/overlay-links/exchange`, {
          method: 'POST',
          body: JSON.stringify({
            k,
            ...(ticketRef.current !== null ? { priorTicket: ticketRef.current } : {})
          }),
          referrerPolicy: 'no-referrer',
          signal: AbortSignal.timeout(EXCHANGE_TIMEOUT_MS)
        })
        if (cancelled) return
        if (!res.ok) {
          if (res.status === 400) {
            log({ stage: 'exchange-invalid', status: res.status, retry: 'none' })
            return
          }
          if (res.status === 403) {
            ticketRef.current = null
            setTicket(null)
            const retryInMs = jittered(RETRY_REFUSED_MS)
            log({ stage: 'exchange-refused', status: res.status, ticket: 'dropped', retryInMs })
            schedule(retryInMs)
            return
          }
          const retryInMs = jittered(res.status === 429 ? RETRY_CAP_MS : RETRY_NETWORK_MS)
          log({ stage: 'exchange-failed', status: res.status, ticket: 'kept', retryInMs })
          schedule(retryInMs)
          return
        }
        const data = await res.json()
        if (cancelled) return
        if (typeof data?.appsyncToken !== 'string' || data.appsyncToken === '') {
          const retryInMs = jittered(RETRY_NETWORK_MS)
          log({ stage: 'exchange-shape', detail: 'response missing appsyncToken', ticket: 'kept', retryInMs })
          schedule(retryInMs)
          return
        }
        const nextTarget = parseTarget(data.target)
        if (requireTarget && nextTarget === null) {
          if (ticketRef.current !== null) {
            const retryInMs = jittered(RETRY_NETWORK_MS)
            log({ stage: 'exchange-shape', detail: 'response missing target', ticket: 'kept', retryInMs })
            schedule(retryInMs)
            return
          }
          log({ stage: 'exchange-no-target', retry: 'none' })
          return
        }
        ticketRef.current = data.appsyncToken
        setTicket(data.appsyncToken)
        setTarget((prev) =>
          prev !== null && nextTarget !== null && prev.kind === nextTarget.kind && prev.id === nextTarget.id
            ? prev
            : nextTarget)
        const ttlSeconds = typeof data.ticketTtlSeconds === 'number' && data.ticketTtlSeconds > 0
          ? data.ticketTtlSeconds
          : DEFAULT_TICKET_TTL_SECONDS
        schedule(ttlSeconds * REFRESH_FRACTION * 1000)
      } catch (err) {
        if (cancelled) return
        const retryInMs = jittered(RETRY_NETWORK_MS)
        log({ stage: 'exchange-error', err: err instanceof Error ? err.name : 'unknown', ticket: 'kept', retryInMs })
        schedule(retryInMs)
      }
    }

    void exchange()
    return () => {
      cancelled = true
      globalThis.clearTimeout(timer)
      ticketRef.current = null
      setTicket(null)
      setTarget(null)
    }
  }, [hasLegacyToken, k, requireTarget])

  return hasLegacyToken
    ? { token: legacyToken, target: null }
    : { token: ticket, target }
}

export const useOverlaySocketCredential = (
  options?: OverlaySocketCredentialOptions
): string | null => useOverlaySocketCredentialState(options).token
